import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import CodeOutlined from '@mui/icons-material/CodeOutlined';
import ListOutlined from '@mui/icons-material/ListOutlined';
import { ASIDE_WIDTH, HEADER_HEIGHT } from '@/theme';
import type { RootState } from '@/store';
import OffersPane from './OffersPane';
import VariablePane from './VariablePane';

const PANELS = [
  { id: 'offers', label: 'Offers', icon: <ListOutlined fontSize='small' />, content: <OffersPane /> },
  { id: 'variables', label: 'Variables', icon: <CodeOutlined fontSize='small' />, content: <VariablePane /> },
];

/** Right-hand utility panel (offers lookup, template variables). Open state lives in Redux (`asideShow`). */
export default function AppAside() {
  const dispatch = useDispatch();
  const asideShow = useSelector((state: RootState) => Boolean(state.asideShow));
  const [tab, setTab] = React.useState(0);

  return (
    <Drawer
      anchor='right'
      variant='persistent'
      open={asideShow}
      sx={{
        width: asideShow ? ASIDE_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: ASIDE_WIDTH,
          boxSizing: 'border-box',
          borderLeft: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        },
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: HEADER_HEIGHT, pr: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_event, value: number) => setTab(value)}
          aria-label='Side panel'
          sx={{ flex: 1, minHeight: HEADER_HEIGHT }}
        >
          {PANELS.map((panel, index) => (
            <Tab
              key={panel.id}
              id={`aside-tab-${panel.id}`}
              aria-controls={`aside-panel-${panel.id}`}
              icon={panel.icon}
              iconPosition='start'
              label={panel.label}
              value={index}
              sx={{ minHeight: HEADER_HEIGHT }}
            />
          ))}
        </Tabs>
        <IconButton
          size='small'
          aria-label='Close side panel'
          onClick={() => dispatch({ type: 'set', asideShow: false })}
        >
          <CloseOutlined />
        </IconButton>
      </Toolbar>
      {PANELS.map((panel, index) => (
        <Box
          key={panel.id}
          role='tabpanel'
          id={`aside-panel-${panel.id}`}
          aria-labelledby={`aside-tab-${panel.id}`}
          hidden={tab !== index}
          sx={{ flex: 1, minHeight: 0, display: tab === index ? 'flex' : 'none', flexDirection: 'column' }}
        >
          {panel.content}
        </Box>
      ))}
    </Drawer>
  );
}
